/**
 * Trading Wallet Hook with Hybrid Balance Updates
 *
 * PROBLEM: After token swaps, users see stale balance data due to RPC node inconsistency.
 * - Swap transactions are sent to Node A
 * - Balance queries hit Node B (which may be lagging behind)
 * - Users see old balances until Node B catches up
 *
 * SOLUTION: Hybrid approach that adapts based on whether tokens are new or existing
 * - Use token server API for fast, cached balance data (normal operation)
 * - After swaps: detect if tokens exist in cache or are newly purchased
 * - EXISTING TOKENS: Use setQueryData with fresh balances for immediate updates
 * - NEW TOKENS: Invalidate cache to fetch complete metadata from token server
 * - All balance queries use transaction block numbers for RPC consistency
 *
 * PERFORMANCE:
 * - Existing token swaps: Only queries the 2 specific tokens involved
 * - New token purchases: Refetches all wallet data to get complete token metadata
 *
 * FRESHNESS: Block-pinned queries ensure consistent data across all RPC nodes
 */

import { useCallback, useMemo } from 'react'
import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosClient } from 'api/apiClient'
import { AxiosResponse } from 'axios'
import { MINUTE_MS } from 'data/constants'
import { useEnvironment } from 'hooks/useEnvironment'
import { useUSDCBalance } from 'hooks/useUsdcBalance'
import { useTownsTokenBalance } from 'use-towns-client'
import { env } from 'utils'
import { type Address } from 'viem'
import { z, ZodType } from 'zod'

import { ChainWalletAssets, TokenAsset, zChainWalletAssets } from '../tradingUtils'

import { useBalanceOnChain } from './useBalanceOnChain'
import { useTokenBalanceOnChain } from './useTokenBalanceOnChain'
import { useTradingWalletAddresses } from './useTradingWalletAddresses'

const zTownsWalletResponse: ZodType<ChainWalletAssets[]> = z.array(zChainWalletAssets)

// Global object to maintain last updated time across all components using this hook
const globalState = {
    lastUpdated: new Date(),
}

// Robust calculation function that handles scientific notation and preserves precision
// Adapted from servers/workers/token-worker/src/handlers/wallet-assets/hydrateHoldingValues.ts
function calculateHoldingValueCents(balance: bigint, decimals: number, priceCents: number): number {
    // Convert priceCents to a string with full fixed-point precision.
    function toDecimalString(num: number): string {
        let str = num.toString()
        // this handles the annoying case where some junk token is priced at like
        // $0.0000000000123 and it gets converted to scientific notation like 1.23e-11
        if (str.includes('e')) {
            str = num.toFixed(50).replace(/\.?0+$/, '')
        }
        return str
    }

    const priceStr = toDecimalString(priceCents)
    // Split into whole and fractional parts,
    // default fractional part to empty string
    const [whole, fraction = ''] = priceStr.split('.')
    const priceScale = 10n ** BigInt(fraction.length)
    const priceInt = BigInt(whole + fraction)

    const tokenScale = 10n ** BigInt(decimals)
    const totalCents = (balance * priceInt) / (tokenScale * priceScale)

    return Number(totalCents)
}

export const useTradingWallet = () => {
    const { evmWalletAddress, solanaWalletAddress } = useTradingWalletAddresses()
    const queryClient = useQueryClient()

    const queryKey = useMemo(
        () => ['walletContents', evmWalletAddress ?? '', solanaWalletAddress ?? ''],
        [evmWalletAddress, solanaWalletAddress],
    )

    const environment = useEnvironment()

    const townsTokenAddress = useMemo(() => {
        return environment.baseChainConfig.addresses.utils.towns
    }, [environment.baseChainConfig.addresses.utils.towns])

    const { invalidateTownsTokenBalance, fetchTownsTokenBalance } = useTownsTokenBalance({
        address: evmWalletAddress as `0x${string}`,
    })

    // USDC balance hooks for reliable USDC detection
    const { address: usdcAddress } = useUSDCMetadata()
    const { data: usdcBalanceData } = useUSDCBalance({
        address: evmWalletAddress as Address,
        enabled: !!evmWalletAddress,
    })

    const { fetchTokenBalance } = useTokenBalanceOnChain(evmWalletAddress, 8453)

    const { fetchETHBalance } = useBalanceOnChain(evmWalletAddress, 8453)
    const { data, isLoading, error } = useQuery({
        queryKey,
        queryFn: async () => {
            const TOKENS_SERVER_URL = env.VITE_TOKEN_SERVER_URL

            const [codexResponse, townsTokenBalance] = await Promise.all([
                axiosClient.get(
                    `${TOKENS_SERVER_URL}/api/towns-wallet?evmWalletAddress=${
                        evmWalletAddress ?? ''
                    }&solanaWalletAddress=${solanaWalletAddress ?? ''}`,
                ),
                townsTokenAddress ? fetchTownsTokenBalance() : Promise.resolve(undefined),
            ])

            const parsedCodexResponse = zTownsWalletResponse.safeParse(
                (codexResponse as AxiosResponse<ChainWalletAssets[]>)?.data ?? [],
            )

            if (!parsedCodexResponse.success) {
                console.error(
                    'Failed to parse codex data from token worker',
                    parsedCodexResponse.error,
                )
                return
            }

            let resultData = parsedCodexResponse.data

            // Helper to check if a token exists in the response
            const hasTokenInResponse = (tokenAddress: string | undefined) =>
                tokenAddress &&
                resultData.some((network) =>
                    network.tokens.some(
                        (token) => token.tokenAddress.toLowerCase() === tokenAddress.toLowerCase(),
                    ),
                )

            // Helper to add a token to Base chain
            const addTokenToBase = (token: TokenAsset) => {
                resultData = resultData.map((network) => ({
                    ...network,
                    tokens: network.chain === '8453' ? [...network.tokens, token] : network.tokens,
                }))
            }

            // Add TOWNS token if missing
            if (
                !hasTokenInResponse(townsTokenAddress) &&
                townsTokenAddress &&
                townsTokenBalance !== undefined &&
                typeof townsTokenBalance === 'bigint'
            ) {
                const _townsToken: TokenAsset = {
                    chain: '8453',
                    tokenAddress: townsTokenAddress,
                    symbol: 'TOWNS',
                    decimals: 18,
                    balance: townsTokenBalance.toString() ?? '0',
                    priceCents: 0,
                    priceChange24h: 0,
                    imageUrl: '',
                    holdingValueCents: 0,
                    name: 'Towns',
                }
                addTokenToBase(_townsToken)
            }

            // Add USDC if missing and user has balance
            if (
                !hasTokenInResponse(usdcAddress) &&
                usdcAddress &&
                usdcBalanceData?.value !== undefined &&
                usdcBalanceData.value > 0n
            ) {
                const holdingValueCents = Math.round(parseFloat(usdcBalanceData.formatted) * 100)
                const _usdcToken: TokenAsset = {
                    chain: '8453',
                    tokenAddress: usdcAddress,
                    symbol: 'USDC',
                    name: 'USD Coin',
                    decimals: 6,
                    balance: usdcBalanceData.value.toString(),
                    priceCents: 100, // $1.00 USD
                    priceChange24h: 0,
                    imageUrl: '',
                    holdingValueCents,
                }
                addTokenToBase(_usdcToken)
            }

            // Update lastUpdated timestamp whenever a successful fetch occurs
            globalState.lastUpdated = new Date()
            return resultData
        },
        enabled: !!evmWalletAddress && !!solanaWalletAddress,
        staleTime: MINUTE_MS * 60 * 24,
    })

    // Function to refresh wallet data by invalidating the cache
    const invalidateWalletQuery = useCallback(async () => {
        await invalidateTownsTokenBalance()
        await queryClient.invalidateQueries({ queryKey })
        globalState.lastUpdated = new Date() // Update timestamp on manual refresh
    }, [queryClient, queryKey, invalidateTownsTokenBalance])

    const updateTokenBalanceAfterSwap = useCallback(
        async (fromTokenAddress: Address, toTokenAddress: Address, blockNumber?: number) => {
            try {
                // Helper function to check if address represents ETH
                const isETHAddress = (address: Address) =>
                    address.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' ||
                    address.toLowerCase() === '0x0000000000000000000000000000000000000000'

                // Helper function to create balance fetch promise
                const createBalanceFetch = (tokenAddress: Address) =>
                    fetchTokenBalance(tokenAddress, blockNumber).then((balance) => ({
                        tokenAddress,
                        balance,
                    }))

                // Check if ETH is involved
                const isETHInvolved = isETHAddress(fromTokenAddress) || isETHAddress(toTokenAddress)

                // Fetch fresh balances for ERC20 tokens only (skip ETH addresses)
                const erc20Addresses = [fromTokenAddress, toTokenAddress].filter(
                    (addr) => !isETHAddress(addr),
                )
                const balancePromises = erc20Addresses.map(createBalanceFetch)

                // Batch all balance fetches together
                const [balanceUpdates, ethBalance] = await Promise.all([
                    Promise.all(balancePromises),
                    fetchETHBalance(blockNumber),
                ])

                // Update ETH balance cache
                const ethQueryKey = ['trading-use-balance', 8453]
                try {
                    queryClient.setQueryData(ethQueryKey, ethBalance)
                } catch (error) {
                    await queryClient.invalidateQueries({
                        queryKey: ethQueryKey,
                    })
                }

                // Check if any swapped tokens are missing from current cache (new tokens)
                const currentData = queryClient.getQueryData(queryKey)
                const parsedCurrentData = zTownsWalletResponse.safeParse(currentData)
                const validCurrentData = parsedCurrentData.success
                    ? parsedCurrentData.data
                    : undefined
                const existingTokens =
                    validCurrentData?.find((n) => n.chain === '8453')?.tokens || []
                const hasNewTokens = erc20Addresses.some(
                    (addr) =>
                        !existingTokens.some(
                            (token) => token.tokenAddress.toLowerCase() === addr.toLowerCase(),
                        ),
                )

                if (hasNewTokens) {
                    // New tokens need full metadata from Codex server
                    await queryClient.invalidateQueries({ queryKey })
                } else {
                    /**
                     * Helper function to update a single token with fresh balance data
                     * Searches for matching balance update and applies it to the token
                     */
                    const updateTokenWithFreshBalance = (token: TokenAsset): TokenAsset => {
                        const balanceUpdate = balanceUpdates.find(
                            (update) =>
                                update.tokenAddress.toLowerCase() ===
                                token.tokenAddress.toLowerCase(),
                        )

                        if (!balanceUpdate) {
                            return token
                        }

                        const updatedToken: TokenAsset = {
                            ...token,
                            balance: balanceUpdate.balance.toString(),
                        }

                        // Recalculate holdingValueCents with robust calculation
                        if (
                            Number.isFinite(updatedToken.decimals) &&
                            updatedToken.decimals >= 0 &&
                            Number.isFinite(updatedToken.priceCents) &&
                            updatedToken.priceCents >= 0
                        ) {
                            // Use robust calculation that handles scientific notation and preserves precision
                            updatedToken.holdingValueCents = calculateHoldingValueCents(
                                BigInt(updatedToken.balance),
                                updatedToken.decimals,
                                updatedToken.priceCents,
                            )
                        }

                        return updatedToken
                    }

                    /**
                     * Efficiently update both tokens and native asset in a single operation
                     */
                    const updateNetwork = (network: ChainWalletAssets): ChainWalletAssets => {
                        // Only update Base chain (8453)
                        if (network.chain !== '8453') {
                            return network
                        }

                        // Update tokens
                        const updatedTokens = network.tokens.map(updateTokenWithFreshBalance)

                        // Update native asset if ETH was involved
                        let updatedNativeAsset = network.nativeAsset
                        if (isETHInvolved) {
                            updatedNativeAsset = {
                                ...network.nativeAsset,
                                balance: ethBalance.toString(),
                            }

                            // Recalculate holdingValueCents for ETH
                            if (
                                Number.isFinite(updatedNativeAsset.priceCents) &&
                                updatedNativeAsset.priceCents >= 0
                            ) {
                                // ETH always has 18 decimals
                                updatedNativeAsset.holdingValueCents = calculateHoldingValueCents(
                                    ethBalance,
                                    18,
                                    updatedNativeAsset.priceCents,
                                )
                            }
                        }

                        return {
                            ...network,
                            tokens: updatedTokens,
                            nativeAsset: updatedNativeAsset,
                        }
                    }

                    // Update the cache with both token and ETH changes in a single operation
                    queryClient.setQueryData(
                        queryKey,
                        (oldData: ChainWalletAssets[] | undefined) => {
                            if (!oldData) {
                                return oldData
                            }

                            return oldData.map(updateNetwork)
                        },
                    )
                }

                globalState.lastUpdated = new Date()
            } catch (error) {
                // Fallback to invalidating the entire query
                await queryClient.invalidateQueries({ queryKey })
            }
        },
        [queryClient, queryKey, fetchTokenBalance, fetchETHBalance],
    )

    const fetchLatestWalletData = useCallback(async () => {
        try {
            return await queryClient.fetchQuery<ChainWalletAssets[]>({ queryKey })
        } catch (error) {
            console.error('Error fetching latest wallet data', error)
            return []
        }
    }, [queryClient, queryKey])

    return {
        data,
        error,
        isLoading,
        invalidateWalletQuery,
        updateTokenBalanceAfterSwap,
        fetchLatestWalletData,
        lastUpdated: globalState.lastUpdated,
    }
}
